<template>
  <v-container fluid class="px-lg-5">
    <app-header :title="title" :actionButton="actionButton" @clicked="exportViolations"
      :breadcrumbs="visibleBreadcrumbs" :showButton="true" />
    <div ref="pdfContent">
      <v-row no-gutters class="border border-2">
        <v-col cols="12" class="p-4 border-bottom border-2" ref="violationsTable">
          <violations-table :attendance-items="attendance.items" />
        </v-col>

        <v-col cols="12" class="p-4 border-bottom border-2" ref="summaryTable">
          <summary-table :attendance-items="attendance.items" />
        </v-col>

        <v-col cols="6" class="p-4 border-end border-2 text-center">
          <v-card class="rounded-card ma-2" flat outlined>
            <v-card-text>
              <div style="height: 400px;" ref="loginChart">
                <attendance-pie-chart :stats="chartData.login_pie_chart" variant="login" :height="350" />
              </div>
              <p>{{ $t('models.dashboard.attributes.loginViolationsDistribution') }}</p>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="6" class="p-4 border-end border-2 text-center">
          <v-card class="rounded-card ma-2" flat outlined>
            <v-card-text>
              <div style="height: 400px;" ref="logoutChart">
                <attendance-pie-chart :stats="chartData.logout_pie_chart" variant="logout" :height="350" />
              </div>
              <p>{{ $t('models.dashboard.attributes.logoutViolationsDistribution') }}</p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import {mapGetters} from 'vuex';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import {jsPDF as JsPDF} from 'jspdf';

import ViolationsTable from './components/ViolationsTable.vue';
import SummaryTable from './components/SummaryTable.vue';
import AttendancePieChart from './components/AttendancePieChart.vue';

export default {
  name: 'AttendanceDashboard',
  components: {
    ViolationsTable,
    SummaryTable,
    AttendancePieChart
  },
  data() {
    return {
      title: {
        text: this.$t('layout.adminMenu.attendanceDashboard'),
        icon: 'mdi-view-dashboard'
      },
      actionButton: {
        text: this.$t('actions.export'),
        icon: 'mdi-arrow-down-bold-box-outline'
      }
    };
  },
  computed: {
    ...mapGetters(['attendance', 'chartData']),
    visibleBreadcrumbs() {
      return [
        {text: this.$t('layout.mainMenu.home'), to: {name: 'Home'}},
        {text: this.$t('layout.mainMenu.attendance'), to: {name: 'AttendanceForm'}},
        {text: this.$t('layout.adminMenu.attendanceDashboard'), to: {name: 'AttendanceDashboard'}}
      ];
    }
  },
  methods: {
    exportViolations() {
      this.$store.commit('setLoading', true);
      const pdf = new JsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        putTotalPages: true
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      let cursorY = margin;

      const sections = [
        {
          ref: 'violationsTable', title: 'Violation Details', type: 'table'
        },
        {
          ref: 'summaryTable', title: 'Summary', type: 'table'
        },
        {
          ref: 'loginChart', title: 'Login Violations Distribution', type: 'chart'
        },
        {
          ref: 'logoutChart', title: 'Logout Violations Distribution', type: 'chart'
        }
      ];

      const allTables = this.$refs.pdfContent?.querySelectorAll('.v-data-table__wrapper');
      const originalStyles = [];
        allTables?.forEach((table) => {
          originalStyles.push({
            element: table,
            style: {
              maxHeight: table.style.maxHeight,
              height: table.style.height,
              overflowY: table.style.overflowY
            }
          });
          table.style.maxHeight = 'none';
          table.style.height = 'auto';
          table.style.overflowY = 'visible';
        });

        this.$nextTick(async () => {
          let isFirstChart = true;
          for (const {
            ref, title, type
          } of sections) {
            const element = this.$refs[ref];
            if (!element) continue;

            if (type === 'table') {
              const headers = [];
              const rows = [];
              const ths = element.querySelectorAll('thead tr th');
              const trs = element.querySelectorAll('tbody tr');

              ths.forEach((th) => headers.push(th.innerText.trim()));
              trs.forEach((tr) => {
                const row = [];
                tr.querySelectorAll('td').forEach((td) => row.push(td.innerText.trim()));
                rows.push(row);
              });

              if (cursorY + 30 > pageHeight - margin) {
                pdf.addPage();
                cursorY = margin;
              }

              pdf.setFontSize(12);
              pdf.setTextColor(40);
              pdf.text(title, margin, cursorY);
              cursorY += 6;

              autoTable(pdf, {
                head: [headers],
                body: rows,
                startY: cursorY,
                margin: {left: margin, right: margin},
                styles: {fontSize: 8},
                headStyles: {fillColor: [80, 80, 80], textColor: [255, 255, 255]},
                didDrawPage: (data) => {
                  cursorY = data.cursor.y + 10;
                }
              });
            } else if (type === 'chart') {
              if (isFirstChart) {
                if (cursorY !== margin) {
                  pdf.addPage();
                  cursorY = margin;
                }
                isFirstChart = false;
              }
              const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#fff'
              });

              const imgData = canvas.toDataURL('image/png');
              const imgProps = pdf.getImageProperties(imgData);
              const imgWidth = pageWidth - 2 * margin;
              const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

              const availableChartArea = pageHeight - 2 * margin - 32;
              const maxChartHeight = Math.max(0, availableChartArea / 2);
              const scaleFactor = Math.min(1, maxChartHeight / imgHeight);
              const scaledWidth = imgWidth * scaleFactor;
              const scaledHeight = imgHeight * scaleFactor;
              const imageX = margin + (imgWidth - scaledWidth) / 2;

              if (cursorY + scaledHeight > pageHeight - margin) {
                pdf.addPage();
                cursorY = margin;
              }

              pdf.setFontSize(12);
              pdf.setTextColor(40);
              pdf.text(title, margin, cursorY);
              cursorY += 6;

              pdf.addImage(imgData, 'PNG', imageX, cursorY, scaledWidth, scaledHeight);
              cursorY += scaledHeight + 10;
            }
          }

          originalStyles.forEach(({element, style}) => {
            element.style.maxHeight = style.maxHeight;
            element.style.height = style.height;
            element.style.overflowY = style.overflowY;
          });

          pdf.save('attendance-dashboard.pdf');
          this.$store.commit('setLoading', false);
        });
    }
  }
};
</script>
